// Every line the console shows, word for word (D-045, D-052). The vocabulary
// and voice are the specification's (docs/design/ux-first-release.md): "stop"
// for ending a share (D-016), "device" never "installation", sentence case,
// no exclamation marks, no "please". www/test/quotes.test.mjs reads this file.

export const copy = {
  product: 'Purlview',
  wordmark: 'purlview',

  titles: {
    signIn: 'Sign in to Purlview',
    shares: 'Shares – Purlview',
    devices: 'Devices – Purlview',
    account: 'Account – Purlview',
    regions: 'Regions – Purlview',
    notFound: 'Not found – Purlview',
  },

  nav: { label: 'Console', shares: 'Shares', devices: 'Devices', account: 'Account', skip: 'Skip to content' },

  signIn: {
    heading: 'Sign in to Purlview',
    email: 'Enter your email to get a code.',
    placeholder: 'name@company.com',
    send: 'Send code',
    sending: 'Sending…',
    sent: (email: string) => `We sent a code to ${email}. It expires in 5 minutes.`,
    codeLabel: 'Code',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    again: 'Send again',
    againIn: (seconds: number) => `Send again in ${seconds}s`,
    otherEmail: 'Use another email',
    invalidEmail: 'Enter a valid email address.',
    wrongCode: (left: number) => `That code didn't match. ${left} ${left === 1 ? 'attempt' : 'attempts'} left.`,
    codeLength: "That code didn't match. Codes have 6 digits.",
    expired: 'That code has expired. Start again.',
    tooMany: (email: string) => `Too many codes were sent to ${email}. Try again later.`,
    noMoreCodes: 'No more codes for this sign-in. Start again.',
    wait: 'Wait for the countdown before asking for another code.',
    firstTime: 'New to Purlview? The first sign-in creates your account.',
  },

  unreachable: "Couldn't reach Purlview. Try again.",
  offline: "Couldn't reach Purlview. Retrying.",
  tryAgain: 'Try again',
  signOut: 'Sign out',
  signingOut: 'Signing out…',
  cancel: 'Cancel',

  status: { ready: 'Sharing', reconnecting: 'Reconnecting', starting: 'Starting' } as Record<string, string>,

  shares: {
    heading: 'Shares',
    live: (n: number) => `${n} live`,
    failed: "Couldn't load your shares",
    empty: 'No active shares',
    emptyLine: 'Run one of these on the machine with the app to start one.',
    firstLine: 'Three steps to your first link.',
    app: 'App',
    from: 'From',
    whoGetsIn: 'Who gets in',
    anyone: 'Anyone with the link',
    access: (n: number, opened: number) =>
      `${n === 1 ? '1 person' : `${n} people`}, ${opened === 0 ? 'none opened' : opened === n ? (n === 1 ? 'opened' : 'all opened') : `${opened} opened`}`,
    people: 'People',
    timeLeft: 'Time left',
    newShare: 'New share',
    newShareLine: 'Shares start from your terminal. Run one of these on the machine with the app; each lasts up to an hour.',
    anyoneCommand: 'Anyone with the link',
    namedCommand: 'Only the people you name',
    left: (time: string) => `${time} left`,
    stops: (time: string) => `Stops ${time}`,
    moreApps: (n: number) => `+${n}`,
    moreAppsSpoken: (n: number) => `and ${n} more ${n === 1 ? 'app' : 'apps'}`,
    serves: 'Serves',
    started: 'Started',
    stopsAt: 'Stops',
    time: 'Time',
    copy: 'Copy link',
    copied: 'Copied',
    copyFailed: "Couldn't copy",
    stop: 'Stop',
    stopShare: 'Stop share',
    stopping: 'Stopping…',
    ask: 'Stop this share?',
    stopFailed: "Couldn't stop this share. Try again.",
    stopped: 'Share stopped',
    invited: 'Invited',
    notSent: 'Invite not sent',
    opened: (time: string) => `Opened ${time}`,
  },

  devices: {
    heading: 'Devices',
    count: (n: number) => `${n} signed in`,
    lead: 'The computers signed in to your account. Signing one out stops its shares.',
    failed: "Couldn't load your devices",
    empty: 'No devices',
    emptyLine: 'Two steps to add the computer your app runs on.',
    device: 'Device',
    signedIn: 'Signed in',
    lastUsed: 'Last used',
    shares: 'Shares',
    never: 'never used',
    today: 'today',
    signOut: 'Sign out device',
    ask: (name: string, shares: number) =>
      shares === 0 ? `Sign out ${name}?` : `Sign out ${name}? Its ${shares} ${shares === 1 ? 'share' : 'shares'} will stop.`,
    failedAction: "Couldn't sign out this device. Try again.",
    signedOut: (name: string) => `${name} signed out`,
    more: 'From your terminal',
    moreLine: 'Run these on any computer with Purlview installed.',
    addCommand: 'Add this computer',
    listCommand: 'The same list, where you can sign one out too',
  },

  account: {
    heading: 'Account',
    lead: 'Your sign-in to Purlview.',
    email: 'Email',
    browsers: 'Browsers',
    browser: 'Browser',
    browsersLine: 'Signed in to this console. Each stays signed in for 30 days.',
    signedOut: (name: string) => `${name} signed out`,
    browsersFailed: "Couldn't load your browsers",
    thisBrowser: 'This browser',
    ask: (name: string) => `Sign out ${name}?`,
    failedAction: "Couldn't sign out this browser. Try again.",
    theme: 'Theme',
    themeLine: 'Dark, like the Purlview website, or choose another.',
    themes: { system: 'System', light: 'Light', dark: 'Dark' },
  },

  regions: {
    heading: 'Regions',
    lead: 'Where should Purlview go next? One vote per region.',
    home: 'US East (Virginia)',
    homeNote: 'Home base.',
    live: 'Live',
    vote: 'Vote',
    voted: 'Voted',
    voting: 'Voting…',
    voteFor: (name: string) => `for ${name}`,
    counted: (name: string) => `Vote counted for ${name}.`,
    picked: (name: string) => `Regions. ${name} is picked: its Vote button has focus.`,
    pickedVoted: (name: string) => `Regions. You already voted for ${name}.`,
    failed: "Couldn't load the regions",
    failedVote: "That vote didn't get through. Try again.",
    wait: "That's a lot of votes at once. Try again in a minute.",
    empty: 'No votes yet. Be the first to put your region on the map.',
    catchingUp: 'Your vote is in. The board catches up in a few seconds.',
    catchingUpLate: 'Your vote is in. The board will show it soon.',
    leading: (name: string) => `${name} is out in front.`,
    tie: 'Neck and neck.',
    outInFront: 'out in front',
    levelAtTop: 'level at the top',
    leaderboard: 'See the leaderboard',
    leaderboardURL: 'https://purlview.com/regions/',
    footnote: 'Votes are tied to your account, and only shares are ever shown.',
    accountHeading: 'Regions',
    accountLine: 'Purlview runs in the eastern US today. Vote for where it goes next.',
    accountLink: 'Vote for a region',
  },

  start: {
    install: 'Install Purlview',
    installURL: 'https://purlview.com/install/',
    installStep: 'Install Purlview',
    installLine: 'On the computer your app runs on.',
    loginStep: 'Sign in there',
    loginLine: 'With the same email, and the code we send it.',
    shareStep: 'Share your app',
    shareLine: 'Name the port it runs on. Each share lasts up to an hour.',
  },

  notFound: {
    heading: "This page doesn't exist",
    line: 'Your shares are one click away.',
    back: 'Go to Shares',
  },

  commands: {
    share: 'purlview share 3000',
    shareTo: 'purlview share 3000 --to name@company.com',
    login: 'purlview login',
    devices: 'purlview devices',
  },
  copyCommand: 'Copy',
  copyCommandFor: (command: string) => `Copy ${command}`,
}
